import React, { useState, useEffect, useCallback } from 'react'
import { DollarSign, BarChart3, TrendingUp, Building2, RefreshCw, LineChart, Eye, Heart, MessageCircle, Share2 } from 'lucide-react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts'
import { Card } from '../ui'
import { DateRangePicker } from '../ui/DateRangePicker'
import { systemDashboardService, SystemTenant, SystemDashboardFilters } from '../../services/systemDashboardService'

interface EmvPoint { date: string; totalEmvValue: number; postCount: number }
interface PlatformEmv { platformId: number; platform: string; totalEmv: number; postCount: number }

// Brand-ish colors per platform (falls back to slate for anything unmapped).
const PLATFORM_COLORS: Record<string, string> = {
  Instagram: '#e1306c',
  TikTok: '#000000',
  'X/Twitter': '#1d9bf0',
  LinkedIn: '#0a66c2',
  YouTube: '#ff0000',
  Facebook: '#1877f2',
  Beehiiv: '#f59e0b',
}
const platformColor = (name: string): string => PLATFORM_COLORS[name] || '#64748b'

/**
 * System Dashboard — cross-tenant analytics for support/system_admin.
 * Aggregates the social metrics across all ACTIVE tenants (org.is_active), with the same
 * filters as the Social Dashboard + a tenant filter to drill into one customer.
 *
 * SCAFFOLD: the tenant dropdown is live; KPI + chart data wire in as the aggregate
 * endpoints land (analytics / emv-over-time / emv-by-date-platform). Fails gracefully.
 */

const formatEMV = (n: number): string => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${(n || 0).toFixed(2)}`
}
const formatNum = (n: number): string => (n || 0).toLocaleString()

const SystemDashboard: React.FC = () => {
  const [tenants, setTenants] = useState<SystemTenant[]>([])
  const [selectedTenant, setSelectedTenant] = useState<string>('') // '' = all active tenants
  const [period, setPeriod] = useState<string>('month')
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null)

  const [analytics, setAnalytics] = useState<any | null>(null)
  const [emvSeries, setEmvSeries] = useState<EmvPoint[]>([])
  const [platformEmv, setPlatformEmv] = useState<PlatformEmv[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load the active-tenant dropdown once.
  useEffect(() => {
    systemDashboardService.getTenants()
      .then((res) => setTenants(res.data || []))
      .catch(() => { /* dropdown just stays "All" */ })
  }, [])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Use the app's standard period-keyword windows (calculatePeriodDates on the BE) so the
    // System Dashboard's date logic matches the rest of the app exactly. The picker maps its
    // "Last 30 Days" preset to the keyword 'month' (which the BE reads as calendar
    // month-to-date), so translate that one to '30days' (rolling 30d, = 31 inclusive days
    // per the app convention). Other presets already match calculatePeriodDates.
    const apiPeriod = period === 'month' ? '30days' : period
    const filters: SystemDashboardFilters = {
      ...(selectedTenant ? { customerId: selectedTenant } : {}),
      ...(period === 'custom' && dateRange
        ? { period: 'custom', customStartDate: dateRange.start, customEndDate: dateRange.end }
        : { period: apiPeriod }),
    }
    try {
      // Single round-trip: KPI totals + EMV-over-time + EMV-by-platform all come from one
      // query. Firing them as separate parallel calls made each re-run the heavy merged-view
      // CTE against the remote DB, which could hang the all-tenants view.
      const res = await systemDashboardService.getOverview(filters)
      const data = res?.data ?? {}
      setAnalytics(data.totals ?? null)
      setEmvSeries((data.dailyData ?? []) as EmvPoint[])
      setPlatformEmv((data.platforms ?? []) as PlatformEmv[])
    } catch {
      // A failed aggregate call — show placeholders, not an error wall.
      setAnalytics(null)
      setEmvSeries([])
      setPlatformEmv([])
      setError('Some aggregate metrics failed to load — try refreshing.')
    } finally {
      setLoading(false)
    }
  }, [selectedTenant, period, dateRange])

  useEffect(() => { loadData() }, [loadData])

  const kpis = [
    { label: 'Total EMV', value: formatEMV(analytics?.totalEmv ?? analytics?.total_emv ?? 0), icon: DollarSign, tint: 'to-emerald-50/30 border-emerald-100', badge: 'from-emerald-500 to-green-600' },
    { label: 'Total Posts', value: formatNum(analytics?.totalPosts ?? analytics?.total_posts ?? 0), icon: BarChart3, tint: 'to-purple-50/30 border-purple-100', badge: 'from-purple-500 to-violet-600' },
    { label: 'Avg EMV', value: formatEMV(analytics?.averageEmv ?? analytics?.avg_emv ?? 0), icon: TrendingUp, tint: 'to-teal-50/30 border-teal-100', badge: 'from-teal-500 to-emerald-600' },
    { label: 'Active Tenants', value: selectedTenant ? '1' : formatNum(tenants.length), icon: Building2, tint: 'to-blue-50/30 border-blue-100', badge: 'from-blue-500 to-indigo-600' },
    { label: 'Total Views', value: formatNum(analytics?.totalViews ?? analytics?.total_views ?? 0), icon: Eye, tint: 'to-sky-50/30 border-sky-100', badge: 'from-sky-500 to-blue-600' },
    { label: 'Total Likes', value: formatNum(analytics?.totalLikes ?? analytics?.total_likes ?? 0), icon: Heart, tint: 'to-rose-50/30 border-rose-100', badge: 'from-rose-500 to-pink-600' },
    { label: 'Total Comments', value: formatNum(analytics?.totalComments ?? analytics?.total_comments ?? 0), icon: MessageCircle, tint: 'to-amber-50/30 border-amber-100', badge: 'from-amber-500 to-orange-600' },
    { label: 'Total Shares', value: formatNum(analytics?.totalShares ?? analytics?.total_shares ?? 0), icon: Share2, tint: 'to-violet-50/30 border-violet-100', badge: 'from-violet-500 to-purple-600' },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">System Dashboard</h2>
          <p className="text-sm text-gray-500">Platform-wide social metrics across all active tenants</p>
        </div>
        <button onClick={loadData} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" title="Refresh">
          <RefreshCw className={`h-4 w-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <select
          value={selectedTenant}
          onChange={(e) => setSelectedTenant(e.target.value)}
          className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All active tenants</option>
          {tenants.map((t) => (
            <option key={t.customerId} value={t.customerId}>{t.name}</option>
          ))}
        </select>
        <DateRangePicker
          value={dateRange}
          selectedPeriod={period}
          maxRetentionDays={365}
          onChange={(range, p) => { setPeriod(p); setDateRange(range) }}
        />
      </div>

      {error && (
        <div className="px-4 py-2 rounded-lg border bg-amber-50 border-amber-200 text-amber-700 text-xs">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-avi-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics data...</p>
          </div>
        </div>
      ) : (
      <>
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ label, value, icon: Icon, tint, badge }) => (
          <Card key={label} className={`p-3 bg-gradient-to-br from-white ${tint} hover:shadow-lg transition-all duration-300 group`}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-[11px] font-semibold text-gray-600 uppercase tracking-wider">{label}</h3>
              <div className={`h-6 w-6 rounded-lg bg-gradient-to-br ${badge} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                <Icon className="h-3 w-3 text-white" />
              </div>
            </div>
            <p className="text-xl font-bold text-gray-900">{loading ? '—' : value}</p>
          </Card>
        ))}
      </div>

      {/* Chart areas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* EMV Over Time */}
        <Card className="p-4 bg-gradient-to-br from-white to-emerald-50/30 border-emerald-100">
          <div className="flex items-center gap-2 mb-3">
            <LineChart className="h-4 w-4 text-emerald-500" />
            <h3 className="text-sm font-semibold text-gray-700">EMV Over Time</h3>
          </div>
          <div className="h-56">
            {emvSeries.some((p) => p.totalEmvValue > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={emvSeries} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sysEmvGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                  <XAxis
                    dataKey="date"
                    fontSize={11}
                    tick={{ fill: '#64748b' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    interval={emvSeries.length > 30 ? Math.ceil(emvSeries.length / 30) - 1 : 0}
                    angle={-45}
                    textAnchor="end"
                    height={50}
                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis
                    fontSize={11}
                    width={60}
                    tick={{ fill: '#64748b' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v.toFixed(0)}`)}
                  />
                  <RechartsTooltip
                    formatter={(value: any, name: string) =>
                      name === 'EMV' ? [formatEMV(value as number), 'EMV'] : [(value as number).toLocaleString(), 'Posts']
                    }
                    labelFormatter={(l) => new Date(l).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalEmvValue"
                    name="EMV"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="url(#sysEmvGradient)"
                    animationDuration={1200}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
                {loading ? 'Loading…' : 'No EMV in the selected period'}
              </div>
            )}
          </div>
        </Card>

        {/* EMV by Platform */}
        <Card className="p-4 bg-gradient-to-br from-white to-purple-50/30 border-purple-100">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-4 w-4 text-purple-500" />
            <h3 className="text-sm font-semibold text-gray-700">EMV by Platform</h3>
          </div>
          <div className="h-56">
            {platformEmv.some((p) => p.totalEmv > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={platformEmv}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} horizontal={false} />
                  <XAxis
                    type="number"
                    fontSize={11}
                    tick={{ fill: '#64748b' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v.toFixed(0)}`)}
                  />
                  <YAxis
                    type="category"
                    dataKey="platform"
                    fontSize={11}
                    width={70}
                    tick={{ fill: '#64748b' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                  />
                  <RechartsTooltip
                    formatter={(value: any, _n: string, props: any) => [
                      `${formatEMV(value as number)} · ${props.payload.postCount} posts`,
                      props.payload.platform,
                    ]}
                    contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid #d1d5db' }}
                  />
                  <Bar dataKey="totalEmv" radius={[0, 4, 4, 0]} animationDuration={1000}>
                    {platformEmv.map((p) => (
                      <Cell key={p.platformId} fill={platformColor(p.platform)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
                {loading ? 'Loading…' : 'No EMV in the selected period'}
              </div>
            )}
          </div>
        </Card>
      </div>
      </>
      )}
    </div>
  )
}

export default SystemDashboard
